/**
 * Shared use-case content — used by both the homepage carousel
 * (UseCases.jsx) and the dedicated /use-cases/<slug> pages
 * (UseCasePage.jsx).
 *
 * Adding a new use case here automatically:
 *   - appears in the homepage carousel with the correct hyperlink,
 *   - gets a dedicated page rendered by UseCasePage.jsx,
 *   - is included in the sitemap.xml (via the prerender ROUTES list),
 *   - shows up in the internal-linking flow for crawlers.
 */
import { Cog, CheckCircle2, Plane, Wind } from 'lucide-react';

const useCases = [
  {
    slug: 'engine-diagnostics',
    icon: Cog,
    title: 'Engine Diagnostics',
    tagline: 'Fuel-nozzle troubleshooting on CFM56 and LEAP engines in under 60 minutes.',
    description:
      'Rapidly identify fuel nozzle leaks on CFM56 and LEAP engines using ultrasound. The UL101 Troubleshooter (B00033) reduces diagnostic time to under 60 minutes, eliminating borescope requirements and preventing unnecessary replacement of multiple components.',
    image:
      '/images/img_0302.jpeg',
    applications: [
      'Reduced diagnostic time to under 60 minutes',
      'Eliminated borescope contamination risk',
      'Identified single failing nozzle vs. replacing all 18',
      'No special certification required for technicians',
    ],
    caseStudy: 'Boeing 737 MAX — CFM LEAP Engine',
    body: [
      {
        heading: 'The problem',
        text: 'A Boeing 737 MAX operator was troubleshooting a suspected fuel-nozzle leak on a CFM LEAP-1B engine. The standard workflow required borescope inspection through combustor access ports, followed by replacement of every one of the eighteen fuel nozzles because there was no way to isolate the failing unit. Both steps drove significant AOG time and parts cost, and the borescope itself carried a contamination risk during the inspection.',
      },
      {
        heading: 'How the UL101 solved it',
        text: 'Using the CTRL UL101 Aviation Troubleshooter, the maintenance team walked the perimeter of the combustor with a solid-probe accessory and listened for the ultrasonic signature of the leaking nozzle. Because a leaking fuel nozzle produces a distinct high-frequency turbulence sound that is completely inaudible to the human ear, the UL101 converted the signal into the audible range and let the technician pinpoint the single failing nozzle in minutes rather than assumption-replace all eighteen.',
      },
      {
        heading: 'The outcome',
        text: 'Diagnostic time dropped from a multi-shift borescope-and-swap event to under 60 minutes. Only the failing nozzle was replaced, so eighteen units of parts cost and labor were avoided. The engine was returned to service the same day with no borescope contamination risk, and the technician who performed the diagnosis needed no additional certification beyond standard UL101 training.',
      },
    ],
    videoUrl: 'https://vimeo.com/1227173730',
    fullVideoUrl: 'https://vimeo.com/1184675327',
    videoPoster:
      '/images/thumb.jpg',
    videoCaption: 'CFM LEAP fuel-nozzle leak isolated in under 60 minutes',
    pdfs: [
      {
        label: 'Download the CFM LEAP 1-B Case Study PDF',
        title: 'CFM LEAP 1-B Fuel Nozzle Inspection, Boeing 737 MAX',
        url: '/docs/case-study-cfm-international-leap-1-b-fuel-nozzle-inspection-boeing-737-max.pdf',
      },
    ],
  },
  {
    slug: 'valve-inspections',
    icon: CheckCircle2,
    title: 'Valve Inspections',
    tagline: 'Isolate faulty flap control, bypass, and solenoid valves before swapping major components.',
    description:
      'Acoustically isolate faulty flap control valves, bypass valves, and solenoids before replacing major components. On a Boeing 737-700 flap-speed defect, a MOC team used the UL101 with a solid probe to compare sound signatures — a good valve was silent, while the faulty bypass valve was unmistakably loud. Targeted diagnosis put the aircraft back in service the same day instead of an overnight AOG.',
    image:
      '/images/img_0321.jpeg',
    applications: [
      '$6,480 saved — accurate bypass valve fix vs. control valve/solenoid swap',
      { text: '108% ROI on a single incident', highlight: true },
      'Troubleshoot time cut by 8 hours (11h → 3h)',
      'Prevented an overnight AOG — same-day return to service',
    ],
    caseStudy: 'Boeing 737-700 — Flap Control & Bypass Valves',
    body: [
      {
        heading: 'The problem',
        text: 'A Boeing 737-700 was reporting a flap-speed defect. Traditional MOC troubleshooting suggested replacing the flap control valve, the bypass valve, and the solenoid — a three-part swap with an estimated 11 hours of hangar time and an overnight AOG risk. The failing component could not be identified before parts had been ordered and installed.',
      },
      {
        heading: 'How the UL101 solved it',
        text: 'The maintenance team fitted the UL101 with a solid contact probe and compared the acoustic signatures of the flap control valve against the bypass valve while cycling the flap system. A healthy control valve was effectively silent, while the bypass valve produced a strong, unambiguous ultrasonic signature — the classic sound of internal fluid bypass under pressure. The failing part was isolated in a single walk-around.',
      },
      {
        heading: 'The outcome',
        text: 'Only the bypass valve was replaced. Troubleshoot time collapsed from 11 hours to 3 hours, $6,480 in unnecessary parts and labor was avoided, and the aircraft returned to service the same day rather than sitting overnight. On a single incident, the UL101 delivered a 108% ROI against its acquisition cost.',
      },
    ],
    videoUrl: 'https://vimeo.com/1184458958',
    videoPoster: '/posters/valve-inspections.jpg',
    videoCaption: 'Boeing 737 flap bypass valve isolated with the UL101 — $6,480 saved, 8 hours cut',
    pdfs: [
      {
        label: 'Download the 737-700 Flap Valve Case Study PDF',
        title: 'Flap Control and Bypass Valves, Boeing 737-700',
        url: '/docs/case-study-flap-control-and-bypass-valves-boeing-737-700.pdf',
      },
    ],
  },
  {
    slug: 'landing-gear-inspections',
    icon: Plane,
    title: 'Landing Gear Inspections',
    tagline: 'Pinpoint nitrogen pressure leaks that traditional methods can\'t detect.',
    description:
      'Detect nitrogen pressure leaks in landing gear systems that are invisible to traditional methods. The UL101 Aviation Troubleshooter pinpointed a defective manifold assembly in a Boeing 777-300ER nose gear, preventing complete pressure loss and potential hard landing while eliminating extended AOG downtime.',
    image:
      '/images/img_3539.jpeg',
    applications: [
      'Identified leak undetectable by traditional methods',
      'Avoided extended AOG downtime',
      'Eliminated need for specialist technician dispatch',
      'Prevented complete landing gear pressure loss',
    ],
    caseStudy: 'Boeing 777-300ER — Nose Landing Gear',
    body: [
      {
        heading: 'The problem',
        text: 'A Boeing 777-300ER was losing nitrogen pressure in the nose landing gear over successive flights. The leak rate was slow enough to be invisible to visual and soap-bubble inspection but fast enough that a complete pressure loss — and a potential hard-landing scenario — was on the horizon. Traditional troubleshooting would have required a specialist technician dispatch and an extended AOG.',
      },
      {
        heading: 'How the UL101 solved it',
        text: 'The line mechanic used the UL101 with an airborne cone accessory to scan the manifold and fittings on the nose gear. A defective manifold assembly produced a clear, localized ultrasonic hiss that the operator could pinpoint within a few feet of scanning. The failing manifold was replaced in place without dispatching a specialist and without waiting for an off-site diagnostic team.',
      },
      {
        heading: 'The outcome',
        text: 'The nose gear was restored to full nitrogen pressure. The airline avoided both the AOG cost and the safety exposure of continued dispatch with a leaking gear. Because the diagnosis and repair were performed by the on-site line team, no specialist dispatch was required.',
      },
    ],
    videoUrl: null,
    pdfs: [
      {
        label: 'Download the 777 Nose Gear Case Study PDF',
        title: 'Pinpointing Nitrogen Leaks in 777 NGL',
        url: '/docs/case-study-pinpointing-nitrogen-leaks-in-777-ngl-.pdf',
      },
    ],
  },
  {
    slug: 'hydraulic-systems',
    icon: Wind,
    title: 'Hydraulic Systems',
    tagline: 'Isolate faulty actuators, control valves, and bypass valves without shotgun swaps.',
    description:
      'Pinpoint faulty hydraulic actuators, control valves, and bypass valves in leading-edge and trailing-edge flap transmissions. The UL101 listens to ultrasonic signatures from friction, blockages, and fluid bypass — isolating specific defective components instead of shotgun-replacing entire assemblies. On a single Boeing 737-700 AOG event, targeted diagnosis avoided replacing all eight LE actuators and delivered a 1,078% ROI on a single UL101.',
    image:
      '/images/c3d796bd-ab8c-4669-bc86-9510e626d1c2.png',
    applications: [
      'LE flap B737-700: 96 → 36 hrs troubleshoot (60 hrs saved)',
      'LE flap AOG: $74,400 total savings vs. replacing all 8 actuators',
      'TE flap B757-200: pinpointed #4 & #5 transmissions vs. trial-and-error swaps',
      'Detects friction, internal leakage, blockages & fluid bypass',
    ],
    caseStudy: 'Boeing 737-700 LE Flap & Boeing 757-200 TE Flap',
    body: [
      {
        heading: 'The problem',
        text: 'On a Boeing 737-700 with an AOG-grounding leading-edge flap defect, the traditional troubleshooting workflow would have replaced all eight leading-edge actuators — the only way to be certain the failing unit was among them. On a separate Boeing 757-200 with a trailing-edge flap disagree, the same shotgun approach would have required trial-and-error swaps of four flap transmissions until the defect cleared.',
      },
      {
        heading: 'How the UL101 solved it',
        text: 'Using the UL101 with a solid contact probe, technicians listened to each actuator and transmission under load. Friction, internal leakage, blockages and fluid bypass each produce distinct ultrasonic signatures that make a failing hydraulic component obvious against its silent, healthy neighbors. On the 737-700, the failing actuator was isolated within one troubleshoot cycle. On the 757-200, transmissions #4 and #5 were pinpointed directly instead of swapping components at random.',
      },
      {
        heading: 'The outcome',
        text: 'The 737-700 troubleshoot dropped from 96 hours to 36 hours (60 hours saved), and $74,400 in unnecessary actuator swaps was avoided — a 1,078% ROI on a single UL101 for this one AOG event. On the 757-200, the flap disagree cleared in a single targeted repair rather than a multi-step trial-and-error swap.',
      },
    ],
    videoUrl: null,
    pdfs: [
      {
        label: 'Download the 737-700 Leading Edge Case Study PDF',
        title: 'Leading Edge Flap Transmission Actuators, Boeing 737-700',
        url: '/docs/case-study-leading-edge-flap-transmission-actuators-boeing-737-700-2.pdf',
      },
      {
        label: 'Download the 757-200 Trailing Edge Case Study PDF',
        title: 'Trailing Edge Flap Disagree, Boeing 757-200',
        url: '/docs/case-study-trailing-edge-flap-disagree-boeing-757-200.pdf',
      },
    ],
  },
];

export default useCases;
export const useCaseBySlug = (slug) => useCases.find((u) => u.slug === slug);
